import { Box } from '@mui/material';
import { styled } from '@mui/styles';
import { Bold } from '@tiptap/extension-bold';
import { Document } from '@tiptap/extension-document';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { BulletList, ListItem } from '@tiptap/extension-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { Text as TextExtension } from '@tiptap/extension-text';
import { Underline } from '@tiptap/extension-underline';
import { Gapcursor, Placeholder, UndoRedo } from '@tiptap/extensions';

import {
  HeadingWithAnchor,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  MenuButtonAddTable,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonEditLink,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuDivider,
  ResizableImage,
  type RichTextEditorRef,
  TableBubbleMenu,
  TableImproved,
  RichTextEditor as TipTapEditor,
  RichTextReadOnly as TitapReadyOnly,
} from 'mui-tiptap';
import React, { useEffect, useMemo, useRef } from 'react';
import { COOL_GRAY700 } from '../../../theme';

const CustomLinkExtension = Link.extend({
  inclusive: false,
});

const extensions = [
  // Note that the Table extension must come before other nodes. See README
  TableImproved.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,

  BulletList,
  Document,
  ListItem,
  Paragraph,
  TextExtension,
  Underline,
  Gapcursor,
  Bold,
  Italic,
  CustomLinkExtension.configure({
    autolink: true,
    linkOnPaste: true,
    openOnClick: false,
  }),
  LinkBubbleMenuHandler,

  // Extensions,
  HeadingWithAnchor.configure({
    // People shouldn't typically need more than 3 levels of headings, so
    // keep a more minimal set (than the default 6) to keep things simpler
    // and less chaotic.
    levels: [1, 2, 3],
  }),

  ResizableImage,
  // We use the regular `History` (undo/redo) extension
  UndoRedo,
];

type RichTextEditorProps = {
  content: string;
  onChange: (content: string) => void;
  height: string | number;
  placeholder?: string;
  onFocus?: () => void;
};

type StyledBoxProps = { height: string | number };

const StyledBox = styled(Box)(({ height }: StyledBoxProps) => ({
  '&& .ProseMirror': {
    height,
    overflowY: 'auto',
    padding: '8px 0px 8px 8px',
  },
  '&& .MuiTiptap-FieldContainer-notchedOutline, && .MuiTiptap-MenuBar-root': {
    border: 'none',
    borderWidth: 0,
  },
  '&& .MuiTiptap-RichTextContent-editable': {
    padding: 0,
  },
  '&& .ProseMirror-gapcursor': {
    paddingTop: '16px',
  },
  '&& .tiptap p.is-editor-empty:first-child::before': {
    color: COOL_GRAY700,
    content: 'attr(data-placeholder)',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
}));

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  height,
  placeholder = '',
  onFocus,
}) => {
  const rteRef = useRef<RichTextEditorRef>(null);

  // make sure the content is updated when it's updated from the outside
  // https://github.com/sjdemartini/mui-tiptap/blob/013d839480df26182cded190cad4eb2f8b47ead0/README.md#re-rendering-richtexteditor-when-content-changes
  const editor = rteRef.current?.editor;
  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }
    if (!editor.isFocused || !editor.isEditable) {
      queueMicrotask(() => {
        const currentSelection = editor.state.selection;
        editor
          .chain()
          .setContent(content)
          .setTextSelection(currentSelection)
          .run();
      });
    }
  }, [content, editor, editor?.isEditable, editor?.isFocused]);

  const editorExtensions = useMemo(
    () => [
      ...extensions,
      Placeholder.configure({
        placeholder,
      }),
    ],
    [placeholder],
  );

  return (
    <StyledBox height={height}>
      <TipTapEditor
        onUpdate={(props) => {
          const text = props.editor.getText();

          // make sure we return empty string if the editor is empty
          // otherwise we get a <p></p> when the editor is empty
          const html = text === '' ? '' : props.editor.getHTML();

          onChange(html);
        }}
        onFocus={onFocus}
        ref={rteRef}
        extensions={editorExtensions}
        content={content}
        renderControls={() => (
          <MenuControlsContainer>
            <MenuButtonBold />
            <MenuButtonItalic />
            <MenuButtonUnderline />
            <MenuDivider />

            <MenuButtonEditLink />

            <MenuDivider />

            <MenuButtonBulletedList />

            <MenuDivider />

            <MenuButtonAddTable />
          </MenuControlsContainer>
        )}
      >
        {() => (
          <>
            <LinkBubbleMenu />
            <TableBubbleMenu />
          </>
        )}
      </TipTapEditor>
    </StyledBox>
  );
};

export const RichTextReadOnly = ({
  content,
  height,
}: {
  content: string;
  height: number | string;
}) => (
  <StyledBox height={height}>
    <TitapReadyOnly content={content} extensions={extensions} />
  </StyledBox>
);
